import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";

export interface Activity {
  id: string;
  tenant_id: string;
  actor_user_id: string | null;
  lead_id: string | null;
  deal_id: string | null;
  contact_id: string | null;
  account_id: string | null;
  activity_type: string;
  direction: string;
  subject: string | null;
  content: string | null;
  ai_sentiment: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export function useActivities(filters?: {
  lead_id?: string;
  deal_id?: string;
  contact_id?: string;
  account_id?: string;
}) {
  return useQuery({
    queryKey: ["activities", filters],
    queryFn: async () => {
      let query = supabase
        .from("activities")
        .select("*")
        .order("created_at", { ascending: false });

      if (filters?.lead_id) {
        query = query.eq("lead_id", filters.lead_id);
      }
      if (filters?.deal_id) {
        query = query.eq("deal_id", filters.deal_id);
      }
      if (filters?.contact_id) {
        query = query.eq("contact_id", filters.contact_id);
      }
      if (filters?.account_id) {
        query = query.eq("account_id", filters.account_id);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;
      return data as Activity[];
    },
  });
}

export function useRealtimeActivities(filters?: {
  lead_id?: string;
  deal_id?: string;
}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("activities-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "activities",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["activities", filters] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, filters]);

  return useActivities(filters);
}

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activity: {
      lead_id?: string;
      deal_id?: string;
      contact_id?: string;
      account_id?: string;
      activity_type: string;
      direction?: string;
      subject?: string;
      content?: string;
      ai_sentiment?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        throw new Error("No tenant assigned");
      }

      const { data, error } = await supabase
        .from("activities")
        .insert({
          lead_id: activity.lead_id,
          deal_id: activity.deal_id,
          contact_id: activity.contact_id,
          account_id: activity.account_id,
          activity_type: activity.activity_type,
          direction: activity.direction || "system",
          subject: activity.subject,
          content: activity.content,
          ai_sentiment: activity.ai_sentiment,
          tenant_id: profile.tenant_id,
          actor_user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useLogActivity() {
  const createActivity = useCreateActivity();

  return {
    logCall: (params: { lead_id?: string; deal_id?: string; contact_id?: string; direction: string; content?: string }) => {
      return createActivity.mutateAsync({
        ...params,
        activity_type: "call",
      });
    },
    logEmail: (params: { lead_id?: string; deal_id?: string; contact_id?: string; direction: string; subject?: string; content?: string }) => {
      return createActivity.mutateAsync({
        ...params,
        activity_type: "email",
      });
    },
    logNote: (params: { lead_id?: string; deal_id?: string; contact_id?: string; content: string }) => {
      return createActivity.mutateAsync({
        ...params,
        activity_type: "note",
        direction: "system",
      });
    },
    logSystem: (params: { lead_id?: string; deal_id?: string; subject: string; content?: string }) => {
      return createActivity.mutateAsync({
        ...params,
        activity_type: "system",
        direction: "system",
      });
    },
  };
}
